import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String, position: Number, upsertUrl: String }

  play() {
    this._loadYouTubeAPI().then(() => this._initPlayer())
  }

  _loadYouTubeAPI() {
    if (window.YT?.Player) return Promise.resolve()
    return new Promise(resolve => {
      if (window._ytApiResolvers) {
        window._ytApiResolvers.push(resolve)
        return
      }
      window._ytApiResolvers = [ resolve ]
      window.onYouTubeIframeAPIReady = () => {
        window._ytApiResolvers.forEach(r => r())
        window._ytApiResolvers = null
      }
      const tag = document.createElement("script")
      tag.src = "https://www.youtube.com/iframe_api"
      document.head.appendChild(tag)
    })
  }

  _initPlayer() {
    const videoId = this.urlValue.match(/embed\/([\w-]+)/)?.[1]
    if (!videoId) return

    const container = document.createElement("div")
    container.style.cssText = "width:100%;height:100%"
    this.element.replaceChildren(container)

    // YT.Player creates an iframe whose `allow` attribute omits "fullscreen".
    // Patch it synchronously via MutationObserver so it's correct before any
    // external code (e.g. system tests) can read the attribute.
    const observer = new MutationObserver((_, obs) => {
      const iframe = this.element.querySelector("iframe")
      if (!iframe) return
      obs.disconnect()
      if (!iframe.allow?.includes("fullscreen")) {
        iframe.allow = iframe.allow ? `${iframe.allow}; fullscreen` : "fullscreen"
      }
    })
    observer.observe(this.element, { childList: true, subtree: true })

    this._player = new YT.Player(container, {
      videoId,
      playerVars: {
        autoplay: 1,
        rel: 0,
        iv_load_policy: 3,
        playsinline: 1,
        fs: 1,
        start: this.positionValue > 5 ? Math.floor(this.positionValue) : 0
      },
      width: "100%",
      height: "100%",
      events: {
        onStateChange: (e) => this._onStateChange(e)
      }
    })
  }

  _onStateChange({ data }) {
    if (data === YT.PlayerState.PLAYING) {
      this._startSaving()
    } else {
      this._stopSaving()
      if (data === YT.PlayerState.PAUSED || data === YT.PlayerState.ENDED) {
        this._saveProgress()
      }
    }
  }

  _startSaving() {
    if (this._saveInterval) return
    this._saveInterval = setInterval(() => this._saveProgress(), 10_000)
  }

  _stopSaving() {
    clearInterval(this._saveInterval)
    this._saveInterval = null
  }

  _saveProgress() {
    if (!this._player || !this.upsertUrlValue) return
    const position = Math.floor(this._player.getCurrentTime?.() ?? 0)
    const duration = Math.floor(this._player.getDuration?.() ?? 0)
    if (position <= 0) return

    fetch(this.upsertUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content
      },
      body: JSON.stringify({ position_seconds: position, duration_seconds: duration })
    })
  }

  disconnect() {
    this._stopSaving()
    this._player?.destroy?.()
  }
}
